import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Pricing = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    analytics.track('Pricing Page View');
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "QuoteTier",
    "description": "Interactive quote builder software with Good/Better/Best pricing packages",
    "offers": [
      {
        "@type": "Offer",
        "name": "Free Plan",
        "price": "0",
        "priceCurrency": "USD",
        "description": "10 sent quotes per month, 3 e-sign acceptances, includes watermark"
      },
      {
        "@type": "Offer",
        "name": "Pro Plan",
        "price": "29",
        "priceCurrency": "USD",
        "priceValidUntil": "2025-12-31",
        "description": "Unlimited quotes, unlimited e-signatures, custom branding, analytics"
      }
    ]
  };

  const faqs = [
    {
      question: "What's included in the Free plan?",
      answer: "The Free plan includes 10 sent quotes and 3 e-sign acceptances per month. Quotes include a small \"Powered by QuoteTier\" watermark. You can connect your Gmail or Microsoft 365 account and use our interactive quote builder."
    },
    {
      question: "How long is the Founders price available?",
      answer: "The $29/month Founders pricing is locked for 90 days for new signups, then it moves to $49/month. Early users who sign up now keep the $29/month rate forever as long as they remain subscribed."
    },
    {
      question: "What happens when I hit my quote limit on Free?",
      answer: "When you reach your monthly limit on the Free plan, you'll see an upgrade prompt. Your existing quotes remain active and clients can still view and sign them. You can upgrade to Pro at any time to unlock unlimited quotes."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your Pro subscription at any time. You'll retain access to Pro features until the end of your billing period. Your data is never deleted, so you can always come back."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a free forever plan — no credit card required. If you sign up for Pro and aren't satisfied within the first 30 days, contact us for a full refund."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe. We also support Apple Pay and Google Pay."
    }
  ];

  return (
    <SEOPageLayout
      title="Pricing - QuoteTier | Free Quote Builder & Pro Plans"
      description="Start free with 3 quotes per month. Upgrade to Pro for $29/mo and get unlimited quotes, e-signatures, custom branding, and analytics. No credit card required to start."
      canonical="/pricing"
      ogTitle="QuoteTier Pricing - Start Free, Scale When Ready"
      ogDescription="Interactive quote builder starting at $0. Free plan includes 3 quotes/month. Pro plan: unlimited everything for $29/mo."
      structuredData={structuredData}
    >
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Simple, <span className="text-primary font-bold">transparent</span> pricing
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed">
              Start free. Upgrade when you're ready to scale.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">

            {/* Free Plan */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 flex flex-col">
              <div className="text-center mb-8">
                <h3 className="font-instrument text-2xl mb-2">Free</h3>
                <div className="text-5xl font-bold">$0</div>
                <div className="text-gray-600 font-jetbrains">/forever</div>
              </div>

              <div className="space-y-4 text-sm font-jetbrains flex-grow">
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span><strong>10 sent quotes/month</strong></span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span><strong>3 e-sign acceptances/month</strong></span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Interactive quote builder</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Good/Better/Best pricing tiers</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Connect Gmail or Microsoft 365</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 border border-black rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Mobile-optimized quotes</span>
                </div>
                <div className="flex items-start space-x-3 text-gray-500">
                  <span className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">!</span>
                  <span className="text-xs">"Powered by QuoteTier" watermark</span>
                </div>
              </div>

              <Link
                to="/signup"
                className="mt-8"
                onClick={() => analytics.track('CTA Clicked', { location: 'pricing', action: 'signup-free' })}
              >
                <Button className="w-full py-4 border-2 border-black text-black text-lg font-bold rounded-lg hover:bg-black hover:text-white transition">
                  Start Free
                </Button>
              </Link>
              <p className="text-center text-xs text-gray-500 mt-3 font-jetbrains">No credit card required</p>
            </div>

            {/* Pro Plan */}
            <div className="bg-white border-2 border-black rounded-2xl p-8 relative flex flex-col">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-black text-white px-4 py-1 text-sm font-bold rounded-full font-jetbrains">
                MOST POPULAR
              </div>

              <div className="text-center mb-8 pt-4">
                <h3 className="font-instrument text-2xl mb-2">Pro</h3>
                <div className="flex items-center justify-center space-x-2">
                  <div className="text-2xl text-gray-400 line-through">$49</div>
                  <div className="text-5xl font-bold text-green-600">$29</div>
                </div>
                <div className="text-gray-600 font-jetbrains">/month</div>
                <div className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full inline-block mt-2 font-jetbrains">
                  Founders Price - Limited Time
                </div>
              </div>

              <div className="space-y-4 text-sm font-jetbrains flex-grow">
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span><strong>Unlimited sent quotes</strong></span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span><strong>Unlimited e-sign acceptances</strong></span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Remove watermark</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Custom branding + logo</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Saved templates</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Analytics & quote tracking</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Managed SMTP + deliverability</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-black text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                  <span>Priority support</span>
                </div>
              </div>

              <Link
                to="/signup"
                className="mt-8"
                onClick={() => analytics.track('CTA Clicked', { location: 'pricing', action: 'signup-pro' })}
              >
                <Button className="w-full py-4 bg-black text-white text-lg font-bold rounded-lg hover:bg-gray-800 transition">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <p className="text-center text-xs text-gray-500 mt-3 font-jetbrains">No credit card required • Cancel anytime</p>
            </div>

          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 md:py-24">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold text-center mb-12">
            Compare Plans
          </h2>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">Feature</th>
                  <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">Free</th>
                  <th className="text-center py-6 px-6 bg-black text-white">
                    <span className="font-instrument text-lg font-bold">Pro</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Quotes per month</td>
                  <td className="py-4 px-6 text-center font-jetbrains">10</td>
                  <td className="py-4 px-6 text-center font-jetbrains font-bold text-green-600">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">E-sign acceptances</td>
                  <td className="py-4 px-6 text-center font-jetbrains">3/month</td>
                  <td className="py-4 px-6 text-center font-jetbrains font-bold text-green-600">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Interactive quote builder</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Good/Better/Best pricing</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Mobile-optimized</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Remove watermark</td>
                  <td className="py-4 px-6 text-center text-gray-400">—</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Custom branding</td>
                  <td className="py-4 px-6 text-center text-gray-400">—</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Saved templates</td>
                  <td className="py-4 px-6 text-center text-gray-400">—</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Analytics & tracking</td>
                  <td className="py-4 px-6 text-center text-gray-400">—</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-jetbrains font-medium">Priority support</td>
                  <td className="py-4 px-6 text-center text-gray-400">—</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full text-left px-6 py-4 flex items-center justify-between font-jetbrains font-medium"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="text-2xl text-gray-400">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 font-jetbrains text-gray-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to win more deals?
          </h2>
          <p className="font-jetbrains text-xl text-gray-600 mb-10">
            Join hundreds of businesses using QuoteTier to increase their average deal size.
          </p>
          <Link
            to="/signup"
            onClick={() => analytics.track('CTA Clicked', { location: 'pricing-bottom', action: 'signup' })}
          >
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
              Start Free Today
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default Pricing;
